/**
 * Units Operations - Funktioner för att hantera marker visibility och rendering
 * 
 * ANSVAR:
 * - Hantera visibility states (ALL, AREA, HIDE)
 * - Renderera markers baserat på visibility och selected areas
 * - Hantera popup när markers klickas
 * - Färglägga unit-pins enligt distrikt (aktivt distrikt har företräde om unit tillhör flera)
 * 
 * INTE ANSVAR FÖR:
 * - React component state
 * - Units data fetching
 * - Filter logic
 */

import LocationHandler from '../ui/location-handler';
import { getUnitsByFeatures } from './units';
import { UnitsVisibility } from '../../types/units-visibility';
import Popup from '../ui/popup';
import * as mapState from '../map/map-state';
import { getCurrentDistrictId, getAllDistrictsForScenario } from '../districts/district-cache';
import { getColoredPinDataUrl } from '../utils/pin-color-utils';

// Closure state
let popup = null;
let visibilityMode = UnitsVisibility.ALL;
/** Latest excluded delfiIds from last renderUnits (for refreshPinColors fast path) */
let currentExcludedIds = new Set();

/**
 * Sätt visibility mode
 * @param {string} mode - Visibility mode (ALL, AREA, HIDE)
 */
export function setVisibilityMode(mode) {
    visibilityMode = mode;
}

/*
 * @param {Array} filteredUnits - Filtered units att rendera
 * @param {Array} selectedAreas - Valda areas (GeoJSON features) - används för AREA mode
 * @param {Object} fetch - Fetch function för popup
 * @param {Object} salesDistrictFilters - Optional: Filter object med unitsIncludeExclude
 * @param {Array} allUnitsOriginal - Optional: Original allUnits array för att hitta inkluderade units från filter
 * @param {boolean} rerender - Om units ska renderas (vid ALL mode)
 * @param {Array} [districtUnitIds] - Optional: companyIds som tillhör distriktet (för pin-färg). Vid ny area använd denna så färgen är synkad.
 * @param {boolean} forceFullRebuild - Om true, tvinga full ombyggnad av markers oavsett läge
 */
export async function renderUnits(filteredUnits, selectedAreas, fetch, salesDistrictFilters = null, allUnitsOriginal = null, rerender = false, districtUnitIds = null, forceFullRebuild = false) {
    const map = mapState.getMap();
    const maps = mapState.getMaps();

    if (!map || !maps) {
        console.warn('units-operations: Map not initialized');
        return;
    }

    const includeExclude = salesDistrictFilters && Array.isArray(salesDistrictFilters.unitsIncludeExclude)
        ? salesDistrictFilters.unitsIncludeExclude
        : [];
    const excludedIds = new Set(
        includeExclude
            .filter(inc => inc && inc.delfiId && inc.include === false)
            .map(inc => inc.delfiId)
    );
    currentExcludedIds = excludedIds;

    // In ALL mode with existing markers and no forced rebuild: only update pin colors
    if (visibilityMode === UnitsVisibility.ALL
        && !forceFullRebuild
        && rerender
        && LocationHandler.markers.length > 0) {
        refreshPinColors(districtUnitIds);
        return;
    }

    if (visibilityMode === UnitsVisibility.AREA) {
        let filteredUnitsInArea = getUnitsByFeatures(selectedAreas, filteredUnits);

        if (includeExclude.length > 0) {
            const included = includeExclude.filter(inc => inc.include);
            if (included.length > 0) {
                const unitsToSearch = allUnitsOriginal || filteredUnits;
                included.forEach(inc => {
                    const unit = unitsToSearch.find(u => u.delfiId === inc.delfiId);
                    if (unit && !filteredUnitsInArea.find(u => u.delfiId === unit.delfiId)) {
                        filteredUnitsInArea.push(unit);
                    }
                });
            }
        }

        if (excludedIds.size > 0 && allUnitsOriginal && selectedAreas && selectedAreas.length > 0) {
            const unitsForArea = getUnitsByFeatures(selectedAreas, allUnitsOriginal);
            const excludedInArea = unitsForArea.filter(
                unit =>
                    unit &&
                    excludedIds.has(unit.delfiId) &&
                    !filteredUnitsInArea.find(u => u.delfiId === unit.delfiId)
            );
            if (excludedInArea.length > 0) {
                filteredUnitsInArea = [...filteredUnitsInArea, ...excludedInArea];
            }
        }

        await renderMarkers(filteredUnitsInArea, map, maps, fetch, districtUnitIds, excludedIds);
    } else if (visibilityMode === UnitsVisibility.AREA_STRICT) {
        let filteredUnitsInArea = getUnitsByFeatures(selectedAreas, filteredUnits);

        if (includeExclude.length > 0) {
            const included = includeExclude.filter(inc => inc.include);
            if (included.length > 0) {
                const unitsToSearch = allUnitsOriginal || filteredUnits;
                included.forEach(inc => {
                    const unit = unitsToSearch.find(u => u.delfiId === inc.delfiId);
                    if (unit && !filteredUnitsInArea.find(u => u.delfiId === unit.delfiId)) {
                        filteredUnitsInArea.push(unit);
                    }
                });
            }
        }

        await renderMarkers(filteredUnitsInArea, map, maps, fetch, districtUnitIds, excludedIds);
    } else if (visibilityMode === UnitsVisibility.ALL) {
        if (rerender) {
            let unitsToShow = [...filteredUnits];

            if (excludedIds.size > 0 && allUnitsOriginal) {
                const seen = new Set(unitsToShow.map(u => u && u.delfiId));
                const excludedUnits = allUnitsOriginal.filter(
                    unit => unit && excludedIds.has(unit.delfiId) && !seen.has(unit.delfiId)
                );
                if (excludedUnits.length > 0) {
                    unitsToShow = [...unitsToShow, ...excludedUnits];
                }
            }

            await renderMarkers(unitsToShow, map, maps, fetch, districtUnitIds, excludedIds);
        }
    } else {
        await hideMarkers();
    }
}

/**
 * Return marker style for a unit: district-colored pin if unit belongs to any visible district.
 * Active district has precedence when unit belongs to multiple districts.
 * Excluded units (include === false) get a white pin with reduced opacity.
 * @param {Object} unit - Unit object (companyId)
 * @param {Array} districtsByPriority - Visible districts with color and units, active first
 * @param {Set} excludedIds - delfiIds marked excluded in filters
 * @returns {Object|null} Style object for LocationHandler or null for default marker
 */
function getMarkerStyleForUnit(unit, districtsByPriority, excludedIds = new Set()) {
    if (!unit) {
        return null;
    }
    if (excludedIds.size > 0 && excludedIds.has(unit.delfiId)) {
        const dataUrl = getColoredPinDataUrl('#FFFFFF');
        if (!dataUrl) {
            return null;
        }
        // Split pin layer: opacity only on pin SVG; logo circle stays solid (see custom-marker--excluded-split)
        return {
            root: {},
            pin: {
                backgroundImage: `url(${dataUrl})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center bottom',
                opacity: '0.7'
            }
        };
    }
    if (!districtsByPriority || districtsByPriority.length === 0) {
        return null;
    }
    const companyIdStr = String(unit.companyId);
    for (let i = 0; i < districtsByPriority.length; i++) {
        const d = districtsByPriority[i];
        if (!d.color || !d.unitSet) continue;
        if (d.unitSet.has(companyIdStr)) {
            const dataUrl = getColoredPinDataUrl(d.color);
            if (!dataUrl) return null;
            return {
                backgroundImage: `url(${dataUrl})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center bottom'
            };
        }
    }
    return null;
}

/**
 * Bygg lista av distrikt med färg och units (aktivt distrikt först). Används för att bestämma pin-färg per unit.
 * @param {Array} [districtUnitIdsOverride] - Optional: companyIds för aktivt distrikt (synk vid ny area)
 * @returns {Array} Array av { color, units } (units som array för att kunna kolla unit.companyId)
 */
function getDistrictsByPriorityForColoring(districtUnitIdsOverride) {
    const currentId = getCurrentDistrictId();
    const all = getAllDistrictsForScenario() || [];
    const visible = all.filter(d => d && d.visible === true && d.color && Array.isArray(d.units) && d.units.length > 0);
    const active = visible.find(d => String(d.id) === String(currentId));
    const others = visible.filter(d => String(d.id) !== String(currentId));
    const ordered = active ? [active, ...others] : others;
    return ordered.map(d => {
        const isActive = String(d.id) === String(currentId);
        const units = isActive && districtUnitIdsOverride != null && Array.isArray(districtUnitIdsOverride)
            ? districtUnitIdsOverride
            : d.units;
        const unitSet = new Set(units.map(id => String(id)));
        return { color: d.color, unitSet };
    });
}

/**
 * Update pin colors on existing markers without removing/recreating them.
 * Used for area toggles in ALL mode where only district membership changes.
 * @param {Array} [districtUnitIdsOverride] - Optional: companyIds for the active district
 */
export function refreshPinColors(districtUnitIdsOverride = null) {
    const districtsByPriority = getDistrictsByPriorityForColoring(districtUnitIdsOverride);
    const hasAnyColor = districtsByPriority.some(d => d.color);
    const getMarkerStyle = (hasAnyColor || currentExcludedIds.size > 0)
        ? (unit) => getMarkerStyleForUnit(
            unit,
            hasAnyColor ? districtsByPriority : [],
            currentExcludedIds
        )
        : null;
    LocationHandler.updateMarkerStyles(getMarkerStyle);
}

/**
 * Renderera markers för en lista av units
 * @param {Array} unitsToShow - Units att visa
 * @param {Object} map - Google Maps map instance
 * @param {Object} maps - Google Maps API instance
 * @param {Object} fetch - Fetch function för popup
 * @param {Array} [districtUnitIdsOverride] - Optional: companyIds för aktivt distrikt. Om angiven används den istället för cache (synkar vid ny area).
 * @param {Set} [excludedIds] - delfiIds excluded from district (white pin styling)
 */
async function renderMarkers(unitsToShow, map, maps, fetch, districtUnitIdsOverride = null, excludedIds = new Set()) {
    if (!map || !maps) {
        console.warn('units-operations: Map not initialized in renderMarkers');
        return;
    }

    if (!unitsToShow || unitsToShow.length === 0) {
        LocationHandler.removeMarkers();
        return;
    }

    const districtsByPriority = getDistrictsByPriorityForColoring(districtUnitIdsOverride);
    const hasAnyDistrictColor = districtsByPriority.some(d => d.color);

    const getMarkerStyle = hasAnyDistrictColor
        ? (unit) => getMarkerStyleForUnit(unit, districtsByPriority, excludedIds)
        : excludedIds.size > 0
            ? (unit) => getMarkerStyleForUnit(unit, [], excludedIds)
            : null;

    await LocationHandler.createLocations({
        map,
        maps,
        units: unitsToShow,
        disableFitBounds: true, // Alltid disable fit bounds under filter-ändringar
        onClick: ({ units: loadedUnits }) => {
            handleMarkerClick(loadedUnits, map, maps, fetch);
        },
        markerStyle: getMarkerStyle
    });
}

/**
 * Hantera marker click - visa popup
 * @param {Array} units - Units att visa i popup
 * @param {Object} map - Google Maps map instance
 * @param {Object} maps - Google Maps API instance
 * @param {Object} fetch - Fetch function för popup
 */
function handleMarkerClick(units, map, maps, fetch) {
    // Ta bort befintlig popup om den finns
    if (popup) {
        popup.remove();
        popup = null;
    }

    // Skapa ny popup
    popup = Popup({
        fetch,
        maps,
        units
    });

    popup.setMap(map);
}

/**
 * Dölj alla markers
 */
async function hideMarkers() {
    LocationHandler.removeMarkers();
}